import React, { useState } from "react";
import { NavigateFunction } from "react-router-dom";
import SubscriptionIcon from "../../../../../images/membership/recurring.svg";
import {
  ContactReferenceModel,
  RelatedSubscriptionGridModel,
  SubscriptionModel,
} from "../models";
import NewSubscriptionPopUp from "./NewSubscriptionPopUp";
import {
  CustomButton,
  ITableHeaders,
  TableWithHeaderButtons,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { SubscriptionUtil } from "../utils";
import { GridNames, SubscriptionStatus } from "../enums";
import PlaceholderSmallerGrid from "./PlaceholderSmallerGrid";
import TableGrid from "./TableGrid";
import { RolesAndPermissionStore } from "../store";
import { AuthService } from "../services";

type IProps = {
  navigate: NavigateFunction;
  subscriptions: RelatedSubscriptionGridModel[];
  selectedContact?: ContactReferenceModel;
  onUpdate: () => void;
  rolesAndPermissionStore: RolesAndPermissionStore;
  isHideHeaderButton?: boolean;
  disableNewButton?: boolean;
};

const RelatedSubscriptionGrid: React.FC<IProps> = ({
  subscriptions,
  navigate,
  selectedContact,
  onUpdate,
  rolesAndPermissionStore,
  isHideHeaderButton,
  disableNewButton,
}) => {
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);

  const headers = () => {
    const columns: ITableHeaders<RelatedSubscriptionGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: RelatedSubscriptionGridModel) => {
          const colors = SubscriptionUtil.getColorsByStatus(data.status);
          return (
            <CustomButton color={colors.color} bgColor={colors.bgColor}>
              {SubscriptionStatus[data.status]}
            </CustomButton>
          );
        },
      },
      { dataKey: "id", title: "Subscription ID" },
      {
        dataKey: "lastPaid",
        title: "Last Paid",
        onCellRender: (data: RelatedSubscriptionGridModel) => {
          return data.lastPaid
            ? UtilityMethods.formatDateInString(data.lastPaid)
            : "-";
        },
      },
      {
        dataKey: "nextDue",
        title: "Next Due",
        onCellRender: (data: RelatedSubscriptionGridModel) => {
          return data.nextDue
            ? UtilityMethods.formatDateInString(data.nextDue)
            : "-";
        },
      },
      {
        dataKey: "pastDueDays",
        title: "Past Due",
        cellColor: "red",
        onCellRender: (data: RelatedSubscriptionGridModel) => {
          return data.pastDueDays > 0 ? data.pastDueDays + " days" : "";
        },
      },
    ];
    return columns;
  };

  const handleNewButtonClick = () => {
    if (!rolesAndPermissionStore.hasManagePermission("Create")) return;
    setIsPopUpOpen(true);
  };

  const handleClose = (refresh: boolean) => {
    setIsPopUpOpen(false);
    if (refresh) {
      onUpdate && onUpdate();
    }
  };

  if (subscriptions.length == 0)
    return (
      <>
        <PlaceholderSmallerGrid
          title="No Subscriptions"
          icon={SubscriptionIcon}
          buttonCaption="New"
          buttonClick={() => handleNewButtonClick()}
          disableButton={disableNewButton === true}
        />
        {isPopUpOpen && (
          <NewSubscriptionPopUp
            onClose={(refresh) => handleClose(refresh)}
            selectedContact={selectedContact ? selectedContact : null}
            subscriptionData={
              new SubscriptionModel({
                selectedContact,
                billingIntervalCount: 1,
                billingEndIntervalCount: 1,
                paymentTime: UtilityMethods.getHHMMFromDate(new Date()),
                paymentMeredium:
                  UtilityMethods.getAMPMFromDate(new Date()) === "AM"
                    ? "AM"
                    : "PM",
              })
            }
          />
        )}
      </>
    );

  return (
    <>
      <TableGrid
        onRowClick={(data: RelatedSubscriptionGridModel) =>
          !AuthService.isMindMeContactRoute &&
          navigate(`/app/subscriptions/detail/${data.id}`)
        }
        wrapperClassName="table-with-header-btns table-header-max-height"
        tableHeader={
          <TableWithHeaderButtons
            isShowHeaderButton={isHideHeaderButton !== true}
            totalItems={subscriptions.length}
            icon={SubscriptionIcon}
            title="Subscriptions"
            onNewButtonClick={handleNewButtonClick} 
          />
        }
        cellSpacing={20}
        headers={headers()}
        data={subscriptions}
        totalItemCount={subscriptions.length}
        name={GridNames.Subscription}
      />
      {isPopUpOpen && (
        <NewSubscriptionPopUp
          onClose={(refresh) => handleClose(refresh)}
          selectedContact={selectedContact ? selectedContact : null}
          subscriptionData={
            new SubscriptionModel({
              selectedContact,
              billingIntervalCount: 1,
              billingEndIntervalCount: 1,
              paymentTime: UtilityMethods.getHHMMFromDate(new Date()),
              paymentMeredium:
                UtilityMethods.getAMPMFromDate(new Date()) === "AM"
                  ? "AM"
                  : "PM",
            })
          }
        />
      )}
    </>
  );
};
export default withRouter(RelatedSubscriptionGrid);
